import React, {useMemo} from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  Card,
  IconButton,
  Button,
  LinearProgress,
  Pagination,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect } from "react";
import { useState } from "react";
import RefreshIcon from '@mui/icons-material/Refresh';
import useMediaQuery from "@mui/material/useMediaQuery";
import Search from "@mui/icons-material/Search";
import Visibility from "@mui/icons-material/Visibility";
import { getAllSalesTargetsAction, getAllDistrictsAction, getAllMandalsAction, getAllVarietiesAction, getAllCropsAction,calculateSalesTargetAction } from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import FarmerTarget from "./farmerTarget";
import SalesTargetDetailsView from "./SalesTargetDetailsView";

function SalesTargetSOMDO() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const [query, setQuery] = React.useState("");
  const [openMonthlyTarget, setOpenMonthlyTarget] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [openViewDialog, setOpenViewDialog] = React.useState(false);
  const [viewRow, setViewRow] = React.useState(null);
  const [recalculatedAcres, setRecalculatedAcres] = useState({});
  const [currentCalcId, setCurrentCalcId] = useState(null);
  const [page, setPage] = React.useState(1);
  const PAGE_SIZE = 30;
  const dispatch = useDispatch();
  const { successData: districtsData } = useSelector((state) => state.getAllDistrictsReducer || {});
  const { successData: mandalsData } = useSelector((state) => state.getAllMandalsReducer || {});
  const { successData } = useSelector(state => state.getAllSalesTargetsReducer || {});
  const { successData: varietiesData } = useSelector(state => state.getAllVarietiesReducer || {});
  const { successData: cropsData } = useSelector(state => state.getAllCropsReducer || {});
  const { successData: calcResponse } = useSelector((state) => state.calculateSalesTargetReducer || {});

  const districtList = useMemo(
    () => districtsData?.data?.districts || [],
    [districtsData]
  );
  const mandalList = useMemo(
    () => mandalsData?.data?.mandals || [],
    [mandalsData]
  );
  const varietyList = useMemo(
    () => varietiesData?.data?.varieties || [],
    [varietiesData]
  );
  const cropList = useMemo(
    () => cropsData?.data?.crops || [],
    [cropsData]
  );

  useEffect(() => {
    dispatch(getAllSalesTargetsAction());
    dispatch(getAllDistrictsAction());
    dispatch(getAllMandalsAction());
    dispatch(getAllVarietiesAction());
    dispatch(getAllCropsAction());
  }, [dispatch]);

  // Strip trailing decimal zeros: "10.00" → "10", "10.50" → "10.5"
  const fmt = (v) => (v != null && v !== '' && v !== '-' ? String(Number(v)) : v ?? '-');

  const getTargetAcresProgress = (row) => {
    const done = parseFloat(row?.targetDone || 0);
    const required = parseFloat(row?.farmerTarget || 0);
    if (required === 0 || !required || required === '-') return 0;
    return Math.round((done / required) * 100);
  };

  const getProgressBarColor = (percentage) => {
    if (percentage >= 100) return '#10B981'; // Green for 100%+
    if (percentage >= 75) return '#3B82F6'; // Blue for 75-99%
    if (percentage >= 50) return '#F59E0B'; // Orange for 50-74%
    return '#EF4444'; // Red for <50%
  };

  // Normalize both flat and nested API response structures into a single flat array
  const salesTargets = React.useMemo(() => {
    const data = successData?.data;
    if (!Array.isArray(data)) return [];
    // Helper: format season
    const formatSeason = (season) => {
      if (!season || typeof season !== "object") return "-";
      const name = season.name || "";
      const year = season.year || "";
      return `${name}${name && year ? " " : ""}${year}`.trim() || "-";
    };
    // Helper: resolve area name
    const resolveAreaName = (target) => {
      if (target.target_area_type === "mandal") {
        const mandal = mandalList.find((m) => m.mandalId === target.target_area_id || m.id === target.target_area_id);
        const district = districtList.find((d) => d.districtId === mandal?.district_id || d.id === mandal?.district_id);
        return district && mandal ? `${district.name} - ${mandal.name}` : mandal ? mandal.name : target.target_area_id;
      } else if (target.target_area_type === "district") {
        const district = districtList.find((d) => d.districtId === target.target_area_id || d.id === target.target_area_id);
        return district ? district.name : target.target_area_id;
      }
      return target.target_area_id;
    };
    // Helper: resolve quantity type from salesTargetDetails
    const resolveQuantityType = (target) => {
      if (!Array.isArray(target.salesTargetDetails) || target.salesTargetDetails.length === 0) return "";
      // Use first detail (or loop for all if needed)
      const detail = target.salesTargetDetails[0];
      if (!detail || !detail.variety_id) return "";
      const variety = varietyList.find(v => v.varietyId === detail.variety_id);
      return variety?.quantity_type || "";
    };
    // Flatten sales_targets and nested mandal sales_targets
    const result = [];
    data.forEach((userItem) => {
      // Handle districts
      if (Array.isArray(userItem?.districts)) {
        userItem.districts.forEach((districtObj) => {
          // District-level sales targets
          if (Array.isArray(districtObj.sales_targets)) {
            districtObj.sales_targets.forEach((target) => {
              const _total1 = parseFloat(target.total_sales_target_qty) || 0;
              const _noAcres1 = parseFloat(target.no_acres_comm) || 0;
              const _targetVal1 = !_noAcres1 ? _total1 : Math.max(0, _total1 - _noAcres1);
              result.push({
                id: target.id,
                season_id: target.season_id ?? target.season?.id ?? null,
                season: formatSeason(target.season),
                target_area_type: target.target_area_type || null,
                areaType: target.target_area_type || "-",
                target_area_id: target.target_area_id ?? null,
                areaName: resolveAreaName(target),
                salesTarget: fmt(target.total_sales_target_qty),
                salesTargetType: resolveQuantityType(target),
                target: _targetVal1,
                no_acres_comm: target.no_acres_comm,
                farmerTarget: fmt(target.farmer_comm_acre_required),
                farmer_comm_acre_required: target.farmer_comm_acre_required,
                targetDone: fmt(target.no_acres_comm),
                avg_qty_per_acre: target.avg_qty_per_acre,
                avg_farmer_purch_qty: target.avg_farmer_purch_qty,
                salesTargetDetails: Array.isArray(target.salesTargetDetails) ? target.salesTargetDetails : [],
              });
            });
          }
          // Mandal-level sales targets
          if (Array.isArray(districtObj.mandals)) {
            districtObj.mandals.forEach((mandalObj) => {
              if (Array.isArray(mandalObj.sales_targets)) {
                mandalObj.sales_targets.forEach((mandalTarget) => {
                  const _total2 = parseFloat(mandalTarget.total_sales_target_qty) || 0;
                  const _noAcres2 = parseFloat(mandalTarget.no_acres_comm) || 0;
                  const _targetVal2 = !_noAcres2 ? _total2 : Math.max(0, _total2 - _noAcres2);
                  result.push({
                    id: mandalTarget.id,
                    season_id: mandalTarget.season_id ?? mandalTarget.season?.id ?? null,
                    season: formatSeason(mandalTarget.season),
                    target_area_type: mandalTarget.target_area_type || null,
                    areaType: mandalTarget.target_area_type || "-",
                    target_area_id: mandalTarget.target_area_id ?? null,
                    areaName: resolveAreaName(mandalTarget),
                    salesTarget: fmt(mandalTarget.total_sales_target_qty),
                    salesTargetType: resolveQuantityType(mandalTarget),
                    target: _targetVal2,
                    no_acres_comm: mandalTarget.no_acres_comm,
                    farmerTarget: fmt(mandalTarget.farmer_comm_acre_required),
                    farmer_comm_acre_required: mandalTarget.farmer_comm_acre_required,
                    targetDone: fmt(mandalTarget.no_acres_comm),
                    avg_qty_per_acre: mandalTarget.avg_qty_per_acre,
                    avg_farmer_purch_qty: mandalTarget.avg_farmer_purch_qty,
                    salesTargetDetails: Array.isArray(mandalTarget.salesTargetDetails) ? mandalTarget.salesTargetDetails : [],
                  });
                });
              }
            });
          }
        });
      }
    });
    return result;
  }, [successData, districtList, mandalList, varietyList]);

  // Rows are already mapped in salesTargets useMemo
  const rows = salesTargets;

  // Search filter: season, areaType, areaName, salesTarget
  const filteredRows = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.season, r.areaType, r.areaName, r.salesTarget].some((v) => String(v).toLowerCase().includes(q))
    );
  }, [query, rows]);

  // Reset to page 1 when search changes
  React.useEffect(() => { setPage(1); }, [query]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const gridColsDesktop = "1.2fr 1.1fr 1.2fr 1.2fr 1.3fr 0.8fr";
  // const handleOpenDialog = () => {
  //   setEditRow(null);
  //   setOpenMonthlyTarget(true);
  // };
  const handleEditDialog = (row) => {
    setEditRow(row);
    setOpenMonthlyTarget(true);
  };
  const handleCloseDialog = () => {
    setOpenMonthlyTarget(false);
    setEditRow(null);
  };
  const handleSubmitDialog = (payload) => {
    setOpenMonthlyTarget(false);
    setEditRow(null);
    dispatch(getAllSalesTargetsAction());
  };
  const handleViewDialog = (row) => {
    setViewRow(row);
    setOpenViewDialog(true);
  };
  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewRow(null);
  };

    const handleRecalculate = (row) => {
    if (!row) return;

    const seasonId = row.season_id ?? row.seasonId ?? row.season?.id;
    const areaType = row.target_area_type ?? row.areaType;
    const areaId = row.target_area_id ?? row.areaId;

    if (!seasonId || !areaType || !areaId) {
      console.error("Missing recalculation parameters", {
        rowId: row.id,
        seasonId,
        areaType,
        areaId,
        row,
      });
      return;
    }

    const payload = {
        id: row.id,
        seasonId,
        areaType,
        areaId,
    };

    console.log("Recalculate Payload:", payload);
    setCurrentCalcId(row.id);

    dispatch(calculateSalesTargetAction({ ...payload, localId: row.id }));
};

useEffect(() => {
  if (calcResponse?.data?.total_expected_acre !== undefined && currentCalcId) {
    setRecalculatedAcres((prev) => ({
      ...prev,
      [currentCalcId]: calcResponse.data.total_expected_acre
    }));
  }
}, [calcResponse, currentCalcId]);

const formatQty = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  return num % 1 === 0 ? num.toFixed(0) : num.toString();
};

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 0 }}>
        Sales Activity
      </Typography>

      <Card sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item size={{ xs: 12, sm: 8, md: 6 }}>
            <TextField
              size="small"
              placeholder="Search by zone name ..."
              slotProps={{
                input: {
                  startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
                },
              }}
              fullWidth
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
            />
          </Grid>
          {/* <Grid item size={{ xs: 12, sm: 4, md: 6 }} sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
            <Button variant="contained" color="primary" sx={{ borderRadius: 2 }} onClick={handleOpenDialog} >
              Update Farmer Target
            </Button>
          </Grid> */}
        </Grid>
      </Card>

      <Box sx={{ mt: 1 }}>
        {isMobile ? (
          <Grid container spacing={2}>
            {pagedRows.map((row) => (
              <Grid item xs={12} key={row.id}>
                <Paper
                  elevation={0}
                  sx={{
                    display: { xs: 'block', sm: 'none' },
                    borderRadius: 3,
                    border: '1px solid #E5E7EB',
                    px: 1,
                    py: 2,
                    mb: 1.5,
                    width: { xs: '100vw', sm: 'auto' },
                    maxWidth: { xs: '100vw', sm: 420 },
                    ml: { xs: '-5px', sm: 0 },
                    boxSizing: 'border-box',
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, minWidth: 130 }}>Season</Typography>
                      <Typography variant="body2">{row.season}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, minWidth: 130 }}>Area type</Typography>
                      <Typography variant="body2">{row.areaType}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, minWidth: 130 }}>Area name</Typography>
                      <Typography variant="body2">{row.areaName}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, minWidth: 130 }}>Sales Target</Typography>
                      <Typography variant="body2">
                        {row.salesTarget}
                        {row.salesTargetType && (
                          <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                            {row.salesTargetType}
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, minWidth: 130 }}>Target Acres</Typography>
                      <Box sx={{ flex: 1 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">{recalculatedAcres[row.id] !== undefined
  ? formatQty(recalculatedAcres[row.id])
  : (row?.no_acres_comm != null ? formatQty(row.no_acres_comm) : '-')
}                                                    /{row.farmerTarget}</Typography>
 <IconButton
        size="small"
        onClick={() => handleRecalculate(row)}
        sx={{
            p: 0.5,
            ml: 0.5
        }}
    >
        <RefreshIcon sx={{ fontSize: 16, color: '#2563EB' }} />
    </IconButton>
                          {row.farmerTarget && row.farmerTarget !== '-' && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: getProgressBarColor(getTargetAcresProgress(row)),
                                fontWeight: 600
                              }}
                            >
                              {getTargetAcresProgress(row)}%
                            </Typography>
                          )}
                        </Box>
                        {row.farmerTarget && row.farmerTarget !== '-' && (
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(getTargetAcresProgress(row), 100)}
                            sx={{
                              mt: 0.5,
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: '#E5E7EB',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: getProgressBarColor(getTargetAcresProgress(row)),
                                borderRadius: 4,
                                transition: 'all 0.3s ease',
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1} mt={1}>
                      {(!row.farmerTarget || row.farmerTarget === '-' || row.farmerTarget === null || row.farmerTarget === undefined) ? (
                        <Button variant="contained" color="primary" size="small" sx={{ borderRadius: 2, minWidth: 0, px: 2, py: 0.5, fontSize: '0.50rem' }} onClick={() => handleEditDialog(row)}>
                          Update Farmer Target
                        </Button>
                      ) : (
                        <IconButton size="small" aria-label="view" onClick={() => handleViewDialog(row)}>
                          <Visibility fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                        </IconButton>
                      )}
                    </Box>
                </Paper>
              </Grid>
            ))}
            {filteredRows.length === 0 && (
              <Grid item xs={12}>
                <Card sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
                  <Typography color="text.secondary">No results found.</Typography>
                </Card>
              </Grid>
            )}
            {totalPages > 1 && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} flexWrap="wrap" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredRows.length)} to {Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} entries
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, val) => setPage(val)}
                    color="primary"
                    size="small"
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box
              sx={{
                display: { xs: "none", sm: "grid" },
                gridTemplateColumns: { sm: gridColsDesktop },
                alignItems: "center",
                px: 1.2,
                py: 1,
                gap: 0.5,
                columnGap: 0.5,
              }}
            >
              {["Season", "Area Type", "Area Name", "Sales Target", "Target Acres", "Actions"].map((h) => (
                <Typography key={h} variant="caption" fontWeight={600} sx={{ textTransform: "uppercase", textAlign: h === "Actions" ? "right" : "left" }}>
                  {h}
                </Typography>
              ))}
            </Box>

            {pagedRows.map((row) => (
              <Paper
                key={row.id}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  display: { xs: "none", sm: "grid" },
                  gridTemplateColumns: { sm: gridColsDesktop },
                  alignItems: "center",
                  border: "1px solid #E5E7EB",
                  px: 2,
                  py: 1.5,
                  gap: 0,
                  columnGap: 0,
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": { borderColor: "#2563EB", boxShadow: "0 4px 14px rgba(37,99,235,0.18)" },
                }}
              >
                <Typography variant="body2" sx={{ lineHeight: 1.1 }}>{row.season}</Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.1 }}>{row.areaType}</Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.1 }}>{row.areaName}</Typography>
                <Typography variant="body2">
                  {row.salesTarget}
                  {row.salesTargetType && (
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                      {row.salesTargetType}
                    </Typography>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
  
  {/* Top row: acres text + percentage only */}
  <Box display="flex" alignItems="center" gap={1}>
    <Typography variant="body2">
      {recalculatedAcres[row.id] !== undefined
  ? formatQty(recalculatedAcres[row.id])
  : (row?.no_acres_comm != null ? formatQty(row.no_acres_comm) : '-')
}
      /
      {row?.farmer_comm_acre_required !== undefined && row?.farmer_comm_acre_required !== null ? formatQty(row.farmer_comm_acre_required) : '-'}
    </Typography>

    <Typography
      variant="caption"
      sx={{
        color: getProgressBarColor(getTargetAcresProgress(row)),
        fontWeight: 600
      }}
    >
      {getTargetAcresProgress(row)}%
    </Typography>
  </Box>

  {/* Bottom row: progress bar + refresh icon */}
  <Box display="flex" alignItems="center" gap={0.5}>
    <Box sx={{ flex: 1 }}>
      <LinearProgress
        variant="determinate"
        value={Math.min(getTargetAcresProgress(row), 100)}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: '#E5E7EB',
          '& .MuiLinearProgress-bar': {
            backgroundColor: getProgressBarColor(getTargetAcresProgress(row)),
            borderRadius: 4,
            transition: 'all 0.3s ease',
          },
        }}
      />
    </Box>
    <Button size="small" sx={{ minWidth: 32, borderColor: 'black', color: '#2563EB', '&:hover': { borderColor: '#2563EB', backgroundColor: 'rgba(37,99,235,0.08)' } }} >
    <IconButton
      size="small"
      onClick={() => handleRecalculate(row)}
      sx={{ p: 0.5, flexShrink: 0 }}
    >
      <RefreshIcon sx={{ fontSize: 16, color: '#2563EB' }} />
    </IconButton>
    </Button>
  </Box>

</Box>
                  
                </Box>
                  <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                    {(!row.farmerTarget || row.farmerTarget === '-' || row.farmerTarget === null || row.farmerTarget === undefined) ? (
                      <Button variant="contained" color="primary" size="small" sx={{ borderRadius: 2, minWidth: 0, px: 2, py: 0.5 , fontSize: '0.70rem'}} onClick={() => handleEditDialog(row)}>
                        Update Farmer Target
                      </Button>
                    ) : (
                      <IconButton size="small" aria-label="view" onClick={() => handleViewDialog(row)}>
                        <Visibility fontSize="small" sx={{ color: "#036144", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.25))" }} />
                      </IconButton>
                    )}
                  </Box>
              </Paper>
            ))}
            {filteredRows.length === 0 && (
              <Card sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
                <Typography color="text.secondary">No results found.</Typography>
              </Card>
            )}
            {totalPages > 1 && (
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredRows.length)} to {Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} entries
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, val) => setPage(val)}
                  color="primary"
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      <FarmerTarget
        open={openMonthlyTarget}
        onClose={handleCloseDialog}
        season={"season"}
        onSubmit={handleSubmitDialog}
        editRow={editRow}
      />

      <SalesTargetDetailsView
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        row={viewRow}
        cropList={cropList}
        varietyList={varietyList}
      />

    </Box>
  );
}

export default SalesTargetSOMDO;
